import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      try {
        if (!user) {
          setUsers([]);
          setLoading(false);
          return;
        }

        // Fetch users from Firestore
        const querySnapshot = await getDocs(collection(db, "users"));
        const usersList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
      } catch (e) {
        console.error("Error fetching users:", e);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user, authLoading]);

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Suspended" : "Active";

      // In a real app, you'd update the user status in Firestore
      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );

      console.log(`User ${userId} status changed to ${newStatus}`);
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const getUserRoleColor = (role) => {
    switch (role) {
      case "Admin":
        return "bg-red-600 text-white";
      case "Moderator":
        return "bg-purple-600 text-white";
      case "User":
        return "bg-blue-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-600 text-white";
      case "Suspended":
        return "bg-red-600 text-white";
      case "Pending":
        return "bg-yellow-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  const getVerificationColor = (level) => {
    switch (level) {
      case "Verified":
        return "bg-green-600 text-white";
      case "Pending":
        return "bg-yellow-600 text-white";
      case "Unverified":
        return "bg-gray-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">Loading users...</span>
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center text-valia-gray-600 py-8 sm:py-12 px-4">
        <svg
          className="w-12 h-12 sm:w-16 sm:h-16 text-valia-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
          />
        </svg>
        <p className="text-base sm:text-lg mb-4">No users found.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-valia-primary mb-2">
              Manage Users
            </h1>
            <p className="text-valia-gray-600 text-sm sm:text-base">
              Manage all registered users
            </p>
          </div>
          <button
            onClick={() => navigate("/add-user")}
            className="valia-btn-primary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add User
          </button>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="block lg:hidden space-y-4">
        {users.map((user) => (
          <div key={user.id} className="valia-card p-4 sm:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-valia-gray-900 truncate">
                  {user.displayName || "Unknown User"}
                </h3>
                <p className="text-valia-gray-600 text-sm truncate">
                  {user.email}
                </p>
              </div>
              <div className="flex gap-2 ml-3">
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(
                    user.status
                  )}`}
                >
                  {user.status}
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getUserRoleColor(
                    user.role
                  )}`}
                >
                  {user.role}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-valia-gray-500 text-xs">Verification</p>
                <p className="text-valia-gray-700 text-sm truncate">
                  {user.verificationLevel}
                </p>
              </div>
              <div>
                <p className="text-valia-gray-500 text-xs">Created</p>
                <p className="text-valia-gray-700 text-sm truncate">
                  {user.createdAt}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => toggleUserStatus(user.id, user.status)}
                className={`flex-1 valia-btn-sm ${
                  user.status === "Active"
                    ? "valia-btn-error"
                    : "valia-btn-success"
                }`}
              >
                {user.status === "Active" ? "Suspend" : "Activate"}
              </button>
              <button
                onClick={() => console.log(`View user ${user.id}`)}
                className="valia-btn-primary valia-btn-sm"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden lg:block">
        <div className="overflow-x-auto bg-white rounded-valia-lg shadow-valia border border-valia-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-valia-gray-50 border-b border-valia-gray-200">
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/4">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                  Verification
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/5">
                  Created Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-valia-gray-200">
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-valia-gray-50 transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-valia-gray-900">
                        {user.displayName || "Unknown User"}
                      </div>
                      <div className="text-valia-gray-600 text-xs">
                        {user.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getUserRoleColor(
                        user.role
                      )}`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        user.status
                      )}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getVerificationColor(
                        user.verificationLevel
                      )}`}
                    >
                      {user.verificationLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-valia-gray-900">
                    {user.createdAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleUserStatus(user.id, user.status)}
                        className={`px-2 py-1 text-xs font-medium rounded-valia transition-colors ${
                          user.status === "Active"
                            ? "bg-valia-error hover:bg-red-600 text-white"
                            : "bg-valia-success hover:bg-green-600 text-white"
                        }`}
                      >
                        {user.status === "Active" ? "Suspend" : "Activate"}
                      </button>
                      <button
                        onClick={() => console.log(`View user ${user.id}`)}
                        className="px-2 py-1 text-xs font-medium bg-valia-primary hover:bg-valia-primary-light text-white rounded-valia transition-colors"
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserList;
